import { FileWarning } from 'lucide-react';
import { useId, useState, type ReactElement } from 'react';

import { InlineError } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';
import { apiErrorReason } from '@/lib/api';
import { usePermission } from '@/hooks/usePermission';

import type { FlowCapabilities } from '../types';
import { useChangeLogs, useSaveChangeLog, type ChangeLog } from '../use-change-log';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

// Control de cambios de la versión en curso (spec 2026-09-11 §3.1): desde la v2 el
// documento no pasa a aprobación sin él. Calidad lo redacta y lo corrige mientras el
// documento no entra en aprobación; ahí queda congelado.

const PERMISSION = 'quality:document:change-log';

function saveErrorCopy(err: Error): string {
  const reason = apiErrorReason(err);
  if (reason === 'change_log_frozen') {
    return 'El documento ya pasó a aprobación y el control de cambios quedó congelado';
  }
  if (reason === 'no_active_flow') {
    return 'El documento ya no tiene un flujo en curso; la pantalla se actualizó';
  }
  return 'No se pudo guardar el control de cambios. Vuelve a intentarlo';
}

function ChangeLogForm({
  entryId,
  version,
  log,
}: {
  entryId: string;
  version: number;
  log: ChangeLog | undefined;
}): ReactElement {
  const id = useId();
  const [summary, setSummary] = useState(log?.summary ?? '');
  const [critical, setCritical] = useState(log?.isCriticalChange ?? false);
  const save = useSaveChangeLog(entryId);
  const { showToast } = useToast();
  const trimmed = summary.trim();
  const unchanged =
    log !== undefined && trimmed === log.summary && critical === log.isCriticalChange;

  const onSave = (): void => {
    save.mutate(
      { summary: trimmed, isCriticalChange: critical },
      {
        onSuccess: () => showToast(`Control de cambios de la v${version} guardado`),
        onError: (err) => showToast(saveErrorCopy(err), 'error'),
      },
    );
  };

  return (
    <>
      <label htmlFor={`${id}-s`} className="fld-lab">
        Qué cambió respecto de la versión anterior
      </label>
      <textarea
        id={`${id}-s`}
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={4}
        maxLength={5000}
        placeholder="Secciones modificadas, requisitos nuevos o eliminados y el motivo del cambio"
        style={{ width: '100%' }}
      />
      <label className="chk">
        <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} />
        Cambio crítico: el personal debe capacitarse de nuevo
      </label>
      <button
        type="button"
        className="btn sm"
        style={{ alignSelf: 'flex-end' }}
        disabled={save.isPending || trimmed === '' || unchanged}
        onClick={onSave}
      >
        {save.isPending
          ? 'Guardando…'
          : log === undefined
            ? 'Guardar control de cambios'
            : 'Actualizar control de cambios'}
      </button>
    </>
  );
}

function ChangeLogReadOnly({ log }: { log: ChangeLog }): ReactElement {
  return (
    <>
      <p style={{ fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>{log.summary}</p>
      {log.isCriticalChange && (
        <span className="badge b-red" style={{ alignSelf: 'flex-start' }}>
          Cambio crítico
        </span>
      )}
    </>
  );
}

export function ChangeLogCard({
  caps,
  companyId,
}: {
  caps: FlowCapabilities;
  companyId: string | null;
}): ReactElement | null {
  const version = caps.targetVersion;
  const applies = caps.flowInstanceId !== null && version !== null && version >= 2;
  const canWrite = usePermission(PERMISSION);
  const query = useChangeLogs(caps.entryId, { companyId, enabled: applies });

  if (!applies) {
    return null;
  }

  const log = (query.data ?? []).find((l) => l.version === version);
  const frozen = caps.currentStatus === 'en_aprobacion' || caps.lockedAt !== null;

  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Control de cambios · v{version}</h3>
      </div>
      {query.isLoading ? (
        <div className="cb" aria-busy="true" aria-label="Cargando el control de cambios">
          <div className={skeletonLine({ width: 90 })} />
          <div className={skeletonLine({ width: 60, space: 'top' })} />
        </div>
      ) : query.isError ? (
        <InlineError icon={<FileWarning />} onRetry={() => void query.refetch()}>
          No se pudo cargar el control de cambios. {errorAdvice(query.error)}
        </InlineError>
      ) : (
        <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {log === undefined && (
            <p className="soft" style={{ fontSize: 12.5, margin: 0 }}>
              Requerido para enviar la v{version} a aprobación.
              {!canWrite && ' El Administrador de Calidad debe registrar qué cambió.'}
            </p>
          )}
          {frozen ? (
            <>
              {log !== undefined && <ChangeLogReadOnly log={log} />}
              <p className="soft" style={{ fontSize: 12, margin: 0 }}>
                En aprobación: el control de cambios ya no se modifica.
              </p>
            </>
          ) : canWrite ? (
            <ChangeLogForm
              key={log?.updatedAt ?? 'nuevo'}
              entryId={caps.entryId}
              version={version}
              log={log}
            />
          ) : (
            log !== undefined && <ChangeLogReadOnly log={log} />
          )}
        </div>
      )}
    </div>
  );
}
